import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, MessageSquare, Printer, RotateCcw, Receipt } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { formatUZS } from '@/lib/format';
import { fiscalColors, tiyinToSom } from '@/lib/fiscal';
import { printFiscalReceipt } from '@/lib/receipt-print';
import { cn } from '@/lib/utils';
import { caseFiscalReceiptKey, useCaseFiscalReceipt } from '@/hooks/use-fiscal-receipts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface Props {
  caseId: number;
  canRetry?: boolean;
  customerName?: string | null;
  customerPhone?: string | null;
  merchantName?: string | null;
}

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border last:border-0 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('text-foreground text-right break-all', mono && 'font-mono text-xs')}>{value || '—'}</span>
    </div>
  );
}

/** FR-104: fiscal receipt of a murabaha sale — status, fiscal sign, QR, print and SMS. */
export function FiscalReceiptPanel({ caseId, canRetry, customerName, customerPhone, merchantName }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: receipt, isLoading } = useCaseFiscalReceipt(caseId);
  const [busy, setBusy] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [phone, setPhone] = useState(customerPhone ?? '');

  if (isLoading) return null;

  if (!receipt) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="fiscal-none">
        <Loader2 size={14} className="animate-spin" /> {t('fiscal.none')}
      </div>
    );
  }

  const refresh = () => qc.invalidateQueries({ queryKey: caseFiscalReceiptKey(caseId) });

  const retry = async () => {
    setBusy(true);
    try {
      const { error } = await apiClient.POST('/fiscal-receipts/{fiscalReceipt}/retry', { params: { path: { fiscalReceipt: receipt.id } } });
      if (error) throw error;
      toast.success(t('fiscal.toast.retried'));
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const sendSms = async () => {
    setBusy(true);
    try {
      const { error } = await apiClient.POST('/fiscal-receipts/{fiscalReceipt}/send-sms', {
        params: { path: { fiscalReceipt: receipt.id } },
        body: { phone: phone.trim() || undefined } as never,
      });
      if (error) throw error;
      toast.success(t('fiscal.toast.sms_sent'));
      setSmsOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const accepted = receipt.status === 'accepted';

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="fiscal-panel">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Receipt size={16} /> {t('fiscal.title')}</h4>
        <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', fiscalColors[receipt.status])}>
          {t(`fiscal.status.${receipt.status}`)}
        </span>
      </div>

      <div>
        <Row label={t('fiscal.amount')} value={formatUZS(tiyinToSom(receipt.amount_tiyin))} />
        <Row label={t('fiscal.vat', { percent: receipt.vat_percent })} value={formatUZS(tiyinToSom(receipt.vat_tiyin))} />
        {accepted && <Row label={t('fiscal.fiscal_sign')} value={receipt.fiscal_sign} mono />}
        {accepted && <Row label={t('fiscal.receipt_id')} value={receipt.receipt_id} mono />}
      </div>

      {accepted && receipt.qr_url && (
        <div className="flex justify-center rounded-lg bg-white p-3" data-testid="fiscal-qr">
          <QRCodeSVG value={receipt.qr_url} size={140} />
        </div>
      )}

      {(receipt.status === 'queued' || receipt.status === 'sent') && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> {t('fiscal.processing')}</p>
      )}

      {receipt.status === 'error' && (
        <p className="text-sm text-destructive">{t('fiscal.failed')}{receipt.error_message ? `: ${receipt.error_message}` : ''}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {accepted && (
          <>
            <Button size="sm" variant="outline" onClick={() => printFiscalReceipt(receipt, { merchantName, customerName })}>
              <Printer size={14} className="mr-1.5" /> {t('fiscal.print')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSmsOpen(true)}>
              <MessageSquare size={14} className="mr-1.5" /> {t('fiscal.send_sms')}
            </Button>
          </>
        )}
        {receipt.status === 'error' && canRetry && (
          <Button size="sm" variant="outline" disabled={busy} onClick={retry}>
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RotateCcw size={14} className="mr-1.5" />} {t('fiscal.retry')}
          </Button>
        )}
      </div>

      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.sms_title')}</DialogTitle>
            <DialogDescription>{t('fiscal.sms_hint')}</DialogDescription>
          </DialogHeader>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+998 90 123 45 67" inputMode="tel" aria-label={t('fiscal.phone')} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsOpen(false)}>{t('common.cancel')}</Button>
            <Button disabled={busy || phone.trim() === ''} onClick={sendSms}>
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} {t('fiscal.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
